import { type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * Badges carry production, stock and invoice STATE.
 *
 * Every variant is a soft tint with a saturated dot, rather than a saturated fill. At fifty rows
 * a column of filled badges is confetti and the eye stops resolving any of them; a column of
 * quiet tints with one red dot in it resolves instantly. That is the entire design.
 *
 * `draft` is not a status — it means "produced by the model, not yet confirmed by a person".
 * See the note on --color-draft, and AGENTS.md ground rule 2.
 */
const badge = tv({
  slots: {
    root: [
      'inline-flex items-center gap-1.5 rounded-pill border py-0.5 pr-2 pl-1.5',
      'text-micro font-semibold whitespace-nowrap',
    ],
    dot: 'size-1.5 shrink-0 rounded-pill',
  },
  variants: {
    variant: {
      neutral: {
        root: 'border-border bg-status-pending-soft text-text-muted',
        dot: 'bg-status-pending',
      },
      active: {
        root: 'border-brand-200 bg-status-active-soft text-brand-800',
        dot: 'bg-status-active',
      },
      done: {
        root: 'border-status-done/25 bg-status-done-soft text-status-done',
        dot: 'bg-status-done',
      },
      warning: {
        root: 'border-status-warning/30 bg-status-warning-soft text-status-warning',
        dot: 'bg-status-warning',
      },
      critical: {
        root: 'border-status-critical/25 bg-status-critical-soft text-status-critical',
        dot: 'bg-status-critical',
      },
      disabled: {
        root: 'border-border bg-surface-sunken text-text-subtle',
        dot: 'bg-text-subtle',
      },
      draft: {
        root: 'border-draft-border bg-draft-soft text-draft',
        dot: 'bg-draft',
      },
      ink: {
        root: 'border-ink bg-ink text-text-inverse',
        dot: 'bg-text-inverse/70',
      },
    },
  },
  defaultVariants: { variant: 'neutral' },
})

export interface BadgeProps
  extends JSX.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badge> {
  /** Set false for a bare label — a badge inside a cell that already has a status column. */
  dot?: boolean
}

export const Badge = (props: BadgeProps) => {
  const [local, rest] = splitProps(props, ['variant', 'dot', 'class', 'children'])
  const styles = () => badge({ variant: local.variant })

  return (
    <span {...rest} class={cn(styles().root(), local.dot === false && 'pl-2', local.class)}>
      <Show when={local.dot !== false}>
        <span class={styles().dot()} aria-hidden="true" />
      </Show>
      {local.children}
    </span>
  )
}
